import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Put } from '@/lib/api';
import { fmtDateTimeIST } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Code2, Save, ExternalLink, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDerivedState } from '@/hooks/use-derived-state';

export default function GlobalSettings() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Global Settings"
        subtitle="Platform-wide defaults for external integrations and code-editor URL. More configuration options are coming soon."
      />

      <Card className="card-elevated" data-testid="platform-defaults-placeholder">
        <CardContent className="p-6 space-y-2">
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold">Platform defaults</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Link-expiry presets, Candidate ID format, data retention, and notification defaults will be
            configurable here soon. For now, these use safe default settings.
          </p>
        </CardContent>
      </Card>

      <CodeEditorSettingCard />
    </div>
  );
}

function CodeEditorSettingCard() {
  const qc = useQueryClient();
  const { data: setting } = useQuery({
    queryKey: ['v1-code-editor-setting'],
    queryFn: () => v1Get('/settings/code-editor'),
  });
  const [draft, setDraft] = useDerivedState(setting?.url || '');

  if (!setting) return null;
  const currentUrl = setting.url || '';
  const dirty = (draft || '') !== currentUrl;
  const trimmed = (draft || '').trim();

  const save = async () => {
    try {
      await v1Put('/settings/code-editor', { url: trimmed || null });
      qc.invalidateQueries({ queryKey: ['v1-code-editor-setting'] });
      toast.success('Code-Editor URL saved');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : (typeof d === 'string' ? d : 'Save failed');
      toast.error(msg);
    }
  };

  return (
    <Card className="card-elevated" data-testid="code-editor-setting-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Code2 className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">External Code Editor</p>
        </div>
        <p className="text-xs text-muted-foreground">
          When a candidate opens a CODE-stage invitation link, this hosted URL is what they&apos;re sent to.
          This platform does NOT host the editor &mdash; set the URL of your externally-hosted instance here.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground">Hosted editor URL</label>
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="https://your-code-editor.example.com/start" data-testid="code-editor-url-input" />
          </div>
          <Button onClick={save} disabled={!dirty} className="gap-1.5" data-testid="code-editor-url-save"><Save className="h-4 w-4" /> Save</Button>
          {setting.url && (
            <a href={setting.url} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-1.5"><ExternalLink className="h-4 w-4" /> Test</Button>
            </a>
          )}
        </div>
        {setting.updated_at && (
          <p className="text-[11px] text-muted-foreground">Last updated {fmtDateTimeIST(setting.updated_at)}</p>
        )}
      </CardContent>
    </Card>
  );
}
